import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import socket from '../socket';
import './css/FinaleDecoderView.css';
import Button from './ui/Button';
import Input from './ui/Input';
import { fetchJson } from '../utils/api';
import { getTeamIdFromPlayer } from '../utils/player';
import { getFinaleRound, normalizeFinaleState, totalSelectableChars } from '../utils/finale';
import useSocketEvent from '../utils/useSocketEvent';

function FinaleDecoderView() {
    const [teams, setTeams] = useState([]);
    const [players, setPlayers] = useState([]);
    const [finale, setFinale] = useState(null);
    const [categoryInput, setCategoryInput] = useState('');
    const [phraseInput, setPhraseInput] = useState('');
    const [showDecoderPrompt, setShowDecoderPrompt] = useState(false);
    const decoderSectionRef = useRef(null);

    const moderatorTeam = useMemo(
        () => teams.find((team) => team.name?.toLowerCase() === 'moderator'),
        [teams]
    );
    const regularTeams = useMemo(
        () => teams.filter((team) => team.id !== moderatorTeam?.id),
        [teams, moderatorTeam]
    );

    useEffect(() => {
        fetchJson('/api/teams')
            .then((data) => setTeams(Array.isArray(data) ? data : []))
            .catch(() => setTeams([]));

        fetchJson('/api/players')
            .then((data) => setPlayers(Array.isArray(data) ? data : []))
            .catch(() => setPlayers([]));
    }, []);

    const requestInitialState = useCallback(() => {
        socket.emit('getQuizState');
        socket.emit('finale:state:request');
    }, []);

    useEffect(() => {
        socket.emit('finale:registerControl');
        console.debug('[FINALE][REGIE] registerControl emitted');
        requestInitialState();
        return () => {
            socket.emit('finale:unregisterControl');
        };
    }, [requestInitialState]);

    useEffect(() => {
        if (socket.connected) {
            requestInitialState();
        }
    }, [requestInitialState]);

    useSocketEvent(socket, 'connect', requestInitialState);

    const handleQuizState = useCallback((state) => {
        if (!state) return;
        if (state.finale !== undefined) {
            setFinale(normalizeFinaleState(state.finale || null));
        }
    }, []);

    const handleFinaleState = useCallback((payload) => {
        if (!payload) return;
        const normalized = normalizeFinaleState(payload.finale || null);
        const round = getFinaleRound(normalized);
        const phrase = round?.phrase ?? '';
        console.debug('[Finale] received state', {
            phraseLen: phrase.length,
            hasPhrase: Boolean(round?.phrase),
        });
        setFinale(normalized);
    }, []);

    const handleTeamsUpdated = useCallback((updatedTeam) => {
        if (!updatedTeam?.id) return;
        setTeams((prev) =>
            prev.map((team) => (team.id === updatedTeam.id ? { ...team, ...updatedTeam } : team))
        );
    }, []);

    const handleTeamCreated = useCallback((newTeam) => {
        if (!newTeam?.id) return;
        setTeams((prev) => [...prev, newTeam]);
    }, []);

    const handleTeamDeleted = useCallback(({ teamId }) => {
        if (!teamId) return;
        setTeams((prev) => prev.filter((team) => team.id !== teamId));
    }, []);

    useSocketEvent(socket, 'quizState', handleQuizState);
    useSocketEvent(socket, 'finale:state', handleFinaleState);
    useSocketEvent(socket, 'teamsUpdated', handleTeamsUpdated);
    useSocketEvent(socket, 'teamCreated', handleTeamCreated);
    useSocketEvent(socket, 'teamDeleted', handleTeamDeleted);

    const currentRound = useMemo(() => getFinaleRound(finale), [finale]);

    useEffect(() => {
        if (currentRound) {
            setCategoryInput(currentRound.category || '');
            setPhraseInput(currentRound.phrase || '');
            return;
        }
        if (!finale?.rounds?.length) {
            setCategoryInput('');
            setPhraseInput('');
        }
    }, [currentRound, finale?.rounds?.length]);

    const visibility = useMemo(() => {
        const baseVisibility = {
            showCategory: false,
            showDecoders: false,
            showPhrase: false,
            showClues: { mode: 'none', teamIds: [] },
        };
        if (!currentRound?.visibility) return baseVisibility;
        const showClues = currentRound.visibility.showClues || baseVisibility.showClues;
        return {
            ...baseVisibility,
            ...currentRound.visibility,
            showClues,
        };
    }, [currentRound?.visibility]);

    const handleToggleActive = () => {
        socket.emit('finale:toggle', { active: !finale?.active });
    };

    const handleRoundChange = (event) => {
        const nextIndex = Number(event.target.value);
        if (!Number.isInteger(nextIndex)) return;
        socket.emit('finale:setRound', { roundIndex: nextIndex });
        setShowDecoderPrompt(false);
    };

    const pickingEnabled = finale?.phase === 'picking';

    const handleTogglePicking = () => {
        socket.emit('finale:setPhase', { phase: pickingEnabled ? 'setup' : 'picking' });
    };

    const handleSaveRound = () => {
        if (finale?.rounds?.length) {
            socket.emit('finale:upsertRound', {
                roundIndex: finale.roundIndex,
                category: categoryInput,
                phrase: phraseInput,
            });
        }
    };

    const handleCreateRound = () => {
        socket.emit('finale:upsertRound', {
            category: categoryInput,
            phrase: phraseInput,
        });
    };

    const handleDeleteRound = () => {
        if (finale?.rounds?.length) {
            socket.emit('finale:deleteRound', { roundIndex: finale.roundIndex });
        }
    };

    const handleNextRound = () => {
        const roundsCount = finale?.rounds?.length || 0;
        if (!roundsCount) return;
        const nextIndex = Math.min((finale?.roundIndex || 0) + 1, roundsCount - 1);
        socket.emit('finale:setRound', { roundIndex: nextIndex });
        setShowDecoderPrompt(true);
    };

    const updateVisibility = (nextVisibility) => {
        if (!finale || !Number.isInteger(finale.roundIndex)) return;
        socket.emit('finale:setVisibility', {
            roundIndex: finale.roundIndex,
            visibility: nextVisibility,
        });
    };

    const updateShowCluesMode = (mode) => {
        updateVisibility({
            ...visibility,
            showClues: {
                mode,
                teamIds: visibility.showClues?.teamIds || [],
            },
        });
    };

    const toggleShowCluesTeam = (teamId) => {
        const current = visibility.showClues?.teamIds || [];
        const nextTeamIds = current.includes(teamId)
            ? current.filter((id) => id !== teamId)
            : [...current, teamId];
        updateVisibility({
            ...visibility,
            showClues: {
                mode: 'perTeam',
                teamIds: nextTeamIds,
            },
        });
    };

    const playersByTeam = useMemo(() => {
        return players.reduce((acc, player) => {
            const teamId = getTeamIdFromPlayer(player);
            if (!teamId) return acc;
            if (!acc[teamId]) acc[teamId] = [];
            acc[teamId].push(player);
            return acc;
        }, {});
    }, [players]);

    const decoderNameForTeam = (teamId, decoderId) => {
        const playersForTeam = playersByTeam[teamId] || [];
        const found = playersForTeam.find((player) => String(player.id) === String(decoderId));
        return found?.name || (decoderId ? `ID ${decoderId}` : '—');
    };

    const handleDecoderChange = (teamId, playerId) => {
        socket.emit('finale:setDecoder', {
            roundIndex: finale?.roundIndex,
            teamId,
            playerId: playerId || null,
        });
    };

    const handleToggleLock = (teamId) => {
        const locked = currentRound?.lockedByTeamId?.[teamId];
        socket.emit('finale:lockPicks', {
            roundIndex: finale?.roundIndex,
            teamId,
            locked: !locked,
        });
    };

    const [pointAdjustments, setPointAdjustments] = useState({});
    const [pickBudgetAdjustments, setPickBudgetAdjustments] = useState({});

    const handleAdjustmentChange = (teamId, value) => {
        setPointAdjustments((prev) => ({
            ...prev,
            [teamId]: value,
        }));
    };

    const handlePickBudgetAdjustmentChange = (teamId, value) => {
        setPickBudgetAdjustments((prev) => ({
            ...prev,
            [teamId]: value,
        }));
    };

    const adjustTeamPoints = (teamId, delta) => {
        const team = teams.find((entry) => entry.id === teamId);
        if (!team) return;
        const nextPoints = Number(team.punkte || 0) + delta;
        fetchJson(`/api/teams/${teamId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ punkte: nextPoints }),
        })
            .then((updatedTeam) => {
                setTeams((prev) =>
                    prev.map((entry) => (entry.id === updatedTeam.id ? updatedTeam : entry))
                );
            })
            .catch((err) => {
                console.error('[adjustTeamPoints] Fehler:', err);
            });
    };

    const adjustTeamPickBudget = (teamId, delta) => {
        if (!teamId) return;
        console.debug('[FINALE][PICKBUDGET] updating', { teamId, delta });
        setTeams((prev) =>
            prev.map((team) => {
                if (team.id !== teamId) return team;
                const currentBudget = Number.isFinite(team.pickBudget) ? team.pickBudget : 0;
                const nextBudget = Math.max(0, currentBudget + delta);
                return { ...team, pickBudget: nextBudget };
            })
        );
        fetchJson(`/api/teams/${teamId}/pick-budget`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ delta }),
        })
            .then((updatedTeam) => {
                if (!updatedTeam?.id) return;
                setTeams((prev) =>
                    prev.map((team) => (team.id === updatedTeam.id ? { ...team, ...updatedTeam } : team))
                );
            })
            .catch((err) => {
                console.error('[FINALE][PICKBUDGET] update failed', err);
            });
    };

    const scrollToDecoderAssignments = () => {
        decoderSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <div className="finale-view">
            <div className="finale-header">
                <div className="finale-header-title">Finale – Transmitter</div>
                <div className="finale-header-row">
                    <div className="finale-toggle">
                        <span>Finale aktiv</span>
                        <Button variant={finale?.active ? 'primary' : 'surface'} onClick={handleToggleActive}>
                            {finale?.active ? 'ON' : 'OFF'}
                        </Button>
                    </div>
                    <label className="finale-round-selector">
                        <span>Runde</span>
                        <select
                            value={Number.isInteger(finale?.roundIndex) ? finale.roundIndex : 0}
                            onChange={handleRoundChange}
                            disabled={!finale?.rounds?.length}
                        >
                            {(finale?.rounds || []).map((round, index) => (
                                <option key={round?.id || index} value={index}>
                                    {index + 1}. {round?.category || 'Ohne Kategorie'}
                                </option>
                            ))}
                        </select>
                    </label>
                    <div className="finale-phase-controls">
                        <Button
                            variant={pickingEnabled ? 'primary' : 'surface'}
                            onClick={handleTogglePicking}
                        >
                            {pickingEnabled ? 'Picking deaktivieren' : 'Picking aktivieren'}
                        </Button>
                    </div>
                </div>
                <div className="finale-header-row">
                    <div className="finale-round-actions">
                        <Button variant="secondary" onClick={handleNextRound}>
                            Nächste Runde
                        </Button>
                        <Button variant="ghost" onClick={handleCreateRound}>
                            Neue Runde
                        </Button>
                        <Button variant="danger" onClick={handleDeleteRound} disabled={!finale?.rounds?.length}>
                            Runde löschen
                        </Button>
                    </div>
                    {showDecoderPrompt && (
                        <div className="finale-decoder-prompt">
                            <span>Decoder wechseln?</span>
                            <Button variant="surface" onClick={() => setShowDecoderPrompt(false)}>
                                Behalten
                            </Button>
                            <Button variant="secondary" onClick={scrollToDecoderAssignments}>
                                Decoder wählen
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            <section className="finale-section">
                <h2>Runde bearbeiten</h2>
                <div className="finale-form-grid">
                    <Input
                        id="finale-category"
                        label="Kategorie"
                        value={categoryInput}
                        onChange={(event) => setCategoryInput(event.target.value)}
                    />
                    <Input
                        id="finale-phrase"
                        label="Phrase"
                        value={phraseInput}
                        onChange={(event) => setPhraseInput(event.target.value)}
                    />
                </div>
                <div className="finale-form-actions">
                    <Button variant="primary" onClick={handleSaveRound} disabled={!finale?.rounds?.length}>
                        Speichern
                    </Button>
                </div>
            </section>

            <section className="finale-section" ref={decoderSectionRef}>
                <h2>Decoder-Zuweisung</h2>
                <div className="finale-team-grid">
                    {regularTeams.map((team) => {
                        const decoderId = currentRound?.decoderByTeamId?.[team.id] || null;
                        const teamPlayers = playersByTeam[team.id] || [];
                        return (
                            <div className="finale-team-card" key={team.id}>
                                <div className="finale-team-header">
                                    <strong>{team.name}</strong>
                                    <span>{decoderNameForTeam(team.id, decoderId)}</span>
                                </div>
                                <select
                                    value={decoderId || ''}
                                    onChange={(event) => handleDecoderChange(team.id, event.target.value || null)}
                                >
                                    <option value="">Kein Decoder</option>
                                    {teamPlayers.map((player) => (
                                        <option key={player.id} value={player.id}>
                                            {player.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        );
                    })}
                </div>
            </section>

            <section className="finale-section">
                <h2>Sichtbarkeit</h2>
                <div className="finale-visibility-grid">
                    <Button
                        variant={visibility.showCategory ? 'primary' : 'surface'}
                        onClick={() => updateVisibility({ ...visibility, showCategory: !visibility.showCategory })}
                    >
                        Kategorie anzeigen
                    </Button>
                    <Button
                        variant={visibility.showDecoders ? 'primary' : 'surface'}
                        onClick={() => updateVisibility({ ...visibility, showDecoders: !visibility.showDecoders })}
                    >
                        Decoder anzeigen
                    </Button>
                    <Button
                        variant={visibility.showPhrase ? 'primary' : 'surface'}
                        onClick={() => updateVisibility({ ...visibility, showPhrase: !visibility.showPhrase })}
                    >
                        Phrase anzeigen
                    </Button>
                    <label>
                        <span>Clues</span>
                        <select
                            value={visibility.showClues?.mode || 'none'}
                            onChange={(event) => updateShowCluesMode(event.target.value)}
                        >
                            <option value="none">Keine</option>
                            <option value="all">Alle Teams</option>
                            <option value="perTeam">Pro Team</option>
                        </select>
                    </label>
                </div>
                {visibility.showClues?.mode === 'perTeam' && (
                    <div className="finale-team-chips">
                        {regularTeams.map((team) => (
                            <Button
                                key={team.id}
                                variant={
                                    visibility.showClues?.teamIds?.includes(team.id) ? 'primary' : 'surface'
                                }
                                onClick={() => toggleShowCluesTeam(team.id)}
                            >
                                {team.name}
                            </Button>
                        ))}
                    </div>
                )}
            </section>

            <section className="finale-section">
                <h2>Team-Punkte</h2>
                <div className="finale-points-grid">
                    {regularTeams.map((team) => {
                        const adjustment = Number(pointAdjustments[team.id] ?? 1);
                        const normalizedAdjustment = Number.isFinite(adjustment) && adjustment > 0 ? adjustment : 1;
                        const pickBudgetAdjustment = Number(pickBudgetAdjustments[team.id] ?? 1);
                        const normalizedPickBudgetAdjustment =
                            Number.isFinite(pickBudgetAdjustment) && pickBudgetAdjustment > 0
                                ? pickBudgetAdjustment
                                : 1;
                        const pickBudgetValue = Number.isFinite(team.pickBudget) ? team.pickBudget : 0;
                        return (
                            <div className="finale-points-card" key={team.id}>
                                <div className="finale-points-header">
                                    <strong>{team.name}</strong>
                                    <span>
                                        {team.punkte ?? 0} Punkte
                                        {finale?.active ? ` | ${pickBudgetValue} PickBudget` : ''}
                                    </span>
                                </div>
                                <div className="finale-points-controls">
                                    <input
                                        type="number"
                                        min="1"
                                        value={normalizedAdjustment}
                                        onChange={(event) => handleAdjustmentChange(team.id, event.target.value)}
                                    />
                                    <Button
                                        variant="primary"
                                        onClick={() => adjustTeamPoints(team.id, normalizedAdjustment)}
                                    >
                                        Plus
                                    </Button>
                                    <Button
                                        variant="surface"
                                        onClick={() => adjustTeamPoints(team.id, -normalizedAdjustment)}
                                    >
                                        Minus
                                    </Button>
                                </div>
                                {finale?.active && (
                                    <div className="finale-pickbudget-controls">
                                        <span className="finale-points-label">PickBudget</span>
                                        <input
                                            type="number"
                                            min="1"
                                            value={normalizedPickBudgetAdjustment}
                                            onChange={(event) =>
                                                handlePickBudgetAdjustmentChange(team.id, event.target.value)
                                            }
                                        />
                                        <Button
                                            variant="primary"
                                            onClick={() => adjustTeamPickBudget(team.id, normalizedPickBudgetAdjustment)}
                                        >
                                            Plus
                                        </Button>
                                        <Button
                                            variant="surface"
                                            onClick={() =>
                                                adjustTeamPickBudget(team.id, -normalizedPickBudgetAdjustment)
                                            }
                                        >
                                            Minus
                                        </Button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </section>

            <section className="finale-section">
                <h2>Teamstatus</h2>
                <div className="finale-team-grid">
                    {regularTeams.map((team) => {
                        const locked = currentRound?.lockedByTeamId?.[team.id];
                        const clue = currentRound?.clueByTeamId?.[team.id] || '';
                        const usedCount = currentRound?.picksByTeamId?.[team.id]?.length || 0;
                        const remainingCount = Math.max(totalSelectableChars(currentRound?.phrase || '') - usedCount, 0);
                        return (
                            <div className="finale-team-card" key={team.id}>
                                <div className="finale-team-header">
                                    <strong>{team.name}</strong>
                                    <span>{locked ? 'Gesperrt' : 'Offen'}</span>
                                </div>
                                <div className="finale-team-meta">
                                    <div>
                                        <span className="label">Clue:</span>
                                        <span className="value">{clue || '—'}</span>
                                    </div>
                                    <div>
                                        <span className="label">Angeklickt:</span>
                                        <span className="value">{usedCount}</span>
                                    </div>
                                    <div>
                                        <span className="label">Übrig:</span>
                                        <span className="value">{remainingCount}</span>
                                    </div>
                                </div>
                                <Button variant="surface" onClick={() => handleToggleLock(team.id)}>
                                    {locked ? 'Entsperren' : 'Sperren'}
                                </Button>
                            </div>
                        );
                    })}
                </div>
            </section>
        </div>
    );
}

export default FinaleDecoderView;
